// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

// Styles
import { PageContainerWrapper } from "src/styles/mui/muiStylesHelper";

// Utils
import { getFormattedDisplayDateTime } from "src/utils/date/dateHelper";

// The privacy policy's own version metadata -- not user data, so no
// timezone conversion, just localized wording/ordering.
const PRIVACY_POLICY_VERSION = "0.0.2";
const PRIVACY_POLICY_CREATED_AT = "2023-01-30T15:25:00";

const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  return (
    <Box
      sx={{
        ...PageContainerWrapper(theme),
        gap: "20px",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Typography fontWeight={600}>{t("Privacy Policy")}</Typography>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography fontSize="14px">
            {t("Version")} {PRIVACY_POLICY_VERSION}
          </Typography>
          <Typography fontSize="14px">
            {t("Created {{ date }}", {
              date: getFormattedDisplayDateTime(
                PRIVACY_POLICY_CREATED_AT,
                i18n.language
              ),
            })}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>Intro</Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              RetinaLogik Inc is fully dedicated to safeguarding your privacy
              and ensuring the security of your personal health information
              (PHI). This Privacy Notice is designed to inform you about how we
              collect, use, disclose, and protect your PHI when you use our
              software and related services. Our primary aim is to ensure your
              clear comprehension of the data we gather and its subsequent
              usage. This privacy declaration is applicable to both our main
              domain and its associated subdomains.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              We acknowledge the sensitivity and significance of personal
              information, necessitating its utmost protection. Within this
              policy, we outline the specifics of the information we collect and
              its purposeful application. Moreover, we expound on our management
              protocols for your information. This Privacy Notice explains your
              rights, choices, and responsibilities concerning the PHI we
              collect and process. It serves as a critical resource to help you
              understand how we handle your data and what you can expect from
              us.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              It is of utmost importance to thoroughly peruse the complete
              policy and gain a comprehensive understanding of the methods
              employed in data collection and protection.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            1. Data Controller and Data Processor
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              RetinaLogik's business customers are the data controllers for most
              of the information that is entered into the RetinaLogik web
              application, website, and supporting systems or that is shared
              periodically with RetinaLogik employees to deliver services. This
              positions RetinaLogik as the data processor for most information
              stored and processed by RetinaLogik. There are some pieces of
              information that are collected directly by RetinaLogik to
              facilitate security, logging, and application performance. These
              items include IP address and behavior within the RetinaLogik
              platform. For these pieces of information, RetinaLogik acts as the
              data controller and processor. Additionally, RetinaLogik employs a
              variety of technologies and partners that periodically act as
              sub-processors (detailed list below). If users have any questions
              or concerns about the processing and handling of their personal
              information, they may reach out to RetinaLogik directly by email
              at privacy@retinalogik.ca.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            2. HIPAA (United States)
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              Where RetinaLogik processes Protected Health Information (PHI) on
              behalf of U.S. healthcare providers, RetinaLogik acts as a
              Business Associate and complies with the HIPAA Privacy and
              Security Rules in accordance with applicable Business Associate
              Agreements. We use and disclose PHI only as permitted by the
              applicable Business Associate Agreement and applicable law.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            3. Canadian Privacy Law (PIPEDA)
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              RetinaLogik complies with the Personal Information Protection and
              Electronic Documents Act (PIPEDA) and, where applicable,
              substantially similar provincial privacy legislation, including
              Alberta's Personal Information Protection Act (PIPA), British
              Columbia's PIPA, and Quebec's Act respecting the protection of
              personal information in the private sector (Law 25), as well as
              applicable provincial health information legislation (such as
              Alberta's Health Information Act) when acting on behalf of
              custodians of health information.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              In accordance with PIPEDA's ten fair information principles,
              RetinaLogik is accountable for personal information under its
              control and has designated a Privacy Officer responsible for
              compliance; identifies the purposes for which personal information
              is collected at or before the time of collection; obtains
              meaningful consent for the collection, use, and disclosure of
              personal information, except where otherwise permitted or required
              by law; limits collection to what is necessary for the identified
              purposes; limits use, disclosure, and retention of personal
              information; maintains personal information that is accurate,
              complete, and up to date; protects personal information with
              safeguards appropriate to its sensitivity; is open about its
              policies and practices; provides individuals with access to their
              personal information upon request; and permits individuals to
              challenge RetinaLogik's compliance with these principles.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            4. Types of Data Collected
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              The RetinaLogik web application and supporting applications
              collect the following types of number, first name, last name,
              province/state, country, ZIP/Postal code, city, address, and
              company name. Complete details on each type of personal data
              collected are provided in the dedicated sections of this Privacy
              Policy or by specific explanation texts displayed before the data
              collection.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              The RetinaLogik web application may collect personal data that the
              user may freely provide, or, in case of usage data, collect when
              using this website, the RetinaLogik web application, and its
              supporting applications. Specific data is required for the
              RetinaLogik web application and supporting applications to provide
              services. If data is mandatory, it is noted throughout the website
              and RetinaLogik web application.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              If the RetinaLogik website or RetinaLogik web application
              specifically states that data is not mandatory, users are free to
              not share this data without consequences to the availability or
              the functioning of the service.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              Users who are uncertain about which personal data is mandatory are
              welcome to contact RetinaLogik at privacy@retinalogik.ca.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              Any use of cookies - or other tracking tools - by the RetinaLogik
              website, the RetinaLogik web application, and its supporting
              applications serves the purpose of providing the service for which
              RetinaLogik has been engaged, in addition to any other purposes
              described in the present document and the Cookie Policy.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              Please note that the specific information collected may vary
              depending on the features and functionalities of our software and
              your interactions with it. Rest assured that we collect and
              process this information in compliance with applicable privacy
              laws.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            5. Mode, Place, and Methods of Processing the Data
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              Data is processed using computers or tech-enabled tools, following
              organizational policies and procedures strictly related to the
              purposes indicated. In some cases, data may be accessible to
              RetinaLogik employees involved with the RetinaLogik website's
              operation, the RetinaLogik web application (platform), and
              supporting applications. Data may also be accessible to external
              parties appointed, if necessary, as data processors or
              sub-processors by RetinaLogik. External parties may include
              third-party technical service providers, hosting providers, and IT
              companies.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              RetinaLogik takes appropriate security measures to prevent
              unauthorized access, disclosure, modification, or data
              destruction. Although we endeavor to employ commercially
              acceptable measures to safeguard your personal information, it's
              important to note that no method of transmission over the Internet
              or electronic storage can guarantee complete security. As a
              result, while we make every efforts to ensure the protection of
              your data, we cannot assure its absolute make sure your passwords
              are a combination of upper case, lower case, letters, and symbols.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              Any information submitted via our platform will be utilized
              exclusively for our internal purposes and will neither be sold nor
              disclosed to any external parties beyond RetinaLogik, in
              accordance with the provisions outlined in this privacy statement.
              With the exception of the scenarios detailed in this privacy
              statement, we shall refrain from selling or disseminating your
              information to any external entities unrelated to RetinaLogik. In
              certain instances, we may engage third-party partners who offer
              services on our behalf to enhance our operational efficacy and
              services we deliver. Such parties are strictly permitted to employ
              the provided information solely to deliver the required services.
              Moreover, the data shared with them will be confined to the
              essential minimum.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              We prioritize the security of the data you transmit to our
              platform by employing industry standard encryption methods,
              ensuring the secure storage and transmission of data. Furthermore,
              we take reasonable precautions to avert unauthorized access
              including role-based access controls, regular access reviews,
              audit logging, and security incident response procedures.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              Furthermore, we take reasonable precautions to avert unauthorized
              access, disclosure, destruction, and alteration of your data
              within our platform. We vigilantly monitor for any such changes
              and promptly implement stringent measures upon detection.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              California residents may request information about the categories
              of personal information collected and the purposes for which it is
              used.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            6. Legal Basis of Processing
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              RetinaLogik may process personal data relating to users if one of
              the following applies:
            </Typography>
            <Box sx={{ fontSize: "14px" }}>
              <ul
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  paddingLeft: "16px",
                }}
              >
                <li>
                  • Users have given their consent for one or more specific
                  purposes. By using our platform you consent and accept to the
                  practices described in the privacy policy.
                </li>
                <li>
                  • Provision of data is necessary for the performance of an
                  agreement with the user. Processing is necessary for
                  compliance with a legal obligation.
                </li>
                <li>• Legitimate interests (security, performance, support)</li>
                <li>
                  • Processing is necessary for the legitimate interests pursued
                  by the controller or by a third party.
                </li>
              </ul>
            </Box>
            <Typography sx={{ fontSize: "14px" }}>
              In any case, RetinaLogik will gladly help clarify the specific
              legal basis that applies to the processing, mainly whether the
              provision of personal data is a statutory or contractual
              requirement or a requirement necessary to enter into a contract.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>7. Place</Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              The data is processed at RetinaLogik's operating policies, hosting
              facilities, and, for some data, third- party sub-processors. The
              majority of data is stored and processed within Canada. In some
              cases data may be stored within the US or EU via third-party
              sub-processors. However, all personal medical data is stored in
              Canada.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              Depending on the user's location, data transfers may involve
              transferring the user's data to a country other than their own. To
              find out more about the processing of such transferred data, users
              can consult the section containing details about the processing of
              personal data. Users are entitled to learn about cross-border data
              transfers. If any such transfer occurs, users can find out more by
              checking the relevant sections of this document or inquiring
              directly with RetinaLogik.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>8. Retention Time</Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              Personal data is processed and stored for as long as required to
              fulfill the purpose for which it is collected.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>Therefore:</Typography>
            <Typography sx={{ fontSize: "14px" }}>
              Personal data collected for the performance of a contract between
              RetinaLogik and a business customer is retained until such
              contract has been entirely performed or the business customer asks
              for the data to be deleted.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              Personal data collected for RetinaLogik's legitimate interests
              shall be retained as long as needed to fulfill such purposes.
              Users may find specific information regarding RetinaLogik's
              legitimate interests within the relevant sections of this document
              or by contacting RetinaLogik.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              RetinaLogik may be allowed to retain personal information for a
              more extended period whenever the user has given consent to such
              processing, as long as such consent is not withdrawn. Furthermore,
              RetinaLogik may be obliged to retain personal data for a more
              extended period whenever required to perform a legal obligation or
              upon order of an authority.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              Once the retention period expires, the user's personal data will
              be securely deleted.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            9. The Purposes of Processing
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              The data concerning the user is collected to allow RetinaLogik to
              provide its services, as well as for the following purposes:
              analytics, user database management, managing contacts and sending
              messages, handling payments, interaction with external social
              networks and platforms, remarketing and behavioral targeting,
              contacting the user, displaying content from external platforms,
              hosting and backend infrastructure, interaction with live chat
              platforms, improving provided services, build new features,
              maintaining and administrating services, and spam protection.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              We may also process your information in order to comply with our
              legal obligation.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              Personal data is collected for the following purposes and using
              the following services and third parties:
            </Typography>
            <Typography sx={{ fontWeight: 600 }}>Analytics</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                The services contained in this section enable RetinaLogik to
                monitor and analyze web traffic and can be used to keep track of
                user behavior.
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
                HubSpot Analytics (HubSpot, Inc.)
              </Typography>
              <Box sx={{ fontSize: "14px", pl: "16px" }}>
                <ul
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <li>
                    • HubSpot Analytics is an analytics service provided by
                    HubSpot, Inc.
                  </li>
                  <li>• Personal data collected: cookies and usage data.</li>
                  <li>
                    • Place of processing: US - [Privacy Policy]
                    (https://www.hubspot.com/legal/privacy-policy)
                  </li>
                </ul>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            10. Contacting the User
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              Mailing List or Newsletter (The RetinaLogik Web Application)
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                By registering on the mailing list or for the newsletter, the
                user's email address will be added to the contact list of those
                who may receive email messages containing information of
                commercial or promotional nature concerning the RetinaLogik web
                application. The user's email address may also be added to this
                list due to signing up via the RetinaLogik website or the
                RetinaLogik web application, or after making a purchase.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                Personal data collected: address, city, company name, cookies,
                country, email address, first name, last name, phone number, job
                role, province, state, usage data, and ZIP/Postal code.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              Phone Contact (The RetinaLogik Web Application)
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                Users that provide their phone number might be contacted for
                commercial or promotional purposes related to the RetinaLogik
                web application or for fulfilling support requests.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                Personal data collected: phone number.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              Contact Form (The RetinaLogik Web Application)
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                By filling in the contact form with their data, users authorize
                the RetinaLogik web application to use these details to reply to
                requests for information, quotes, or any other kind of request
                as indicated by the form's header.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                Personal data collected: address, city, company name, country,
                email address, first name, last name, phone number, job role,
                province/state, and ZIP/Postal code.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            11. Displaying Content from External Platforms
          </Typography>

          <Typography sx={{ fontSize: "14px", pl: "16px" }}>
            This type of service allows users to view content hosted on external
            platforms directly from the pages of the RetinaLogik web application
            and interact with them.
          </Typography>
          <Typography sx={{ fontSize: "14px", pl: "16px" }}>
            This type of service might still collect web traffic data for the
            pages where the service is installed, even when users do not use it.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              Google Fonts (Google Inc.)
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                Google Fonts is a typeface visualization service provided by
                Google Inc. that allows this Application to incorporate content
                of this kind on its pages.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                Personal data collected: usage data and various types of data as
                specified in the service's privacy policy.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                Place of processing: US - [Privacy Policy]
                (https://www.google.it/intl/policies/privacy/). Privacy Shield
                participant.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>Handling Payments</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                Payment processing services enable the RetinaLogik web
                application to process payments by credit card, bank transfer,
                or other means. The RetinaLogik web application shares only the
                information necessary to execute the transaction with the
                financial intermediaries handling the transaction. Some of these
                services may also enable sending timed messages to the user,
                such as emails containing invoices or notifications concerning
                the payment.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              Stripe (Stripe Inc.)
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                Stripe is a payment service provided by Stripe Inc.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                Personal Data collected: various types of Data as specified in
                the privacy policy of the service. Place of processing: US -
                [Privacy Policy] (https://stripe.com/gb/privacy).
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              Hosting and Back-End Infrastructure
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                This type of service has the purpose of hosting data and files
                that enable the RetinaLogik website and the RetinaLogik web
                application to run and be distributed. Additionally, these
                services provide the infrastructure to run specific features or
                parts of the application. Some of these services work through
                geographically distributed servers, making it difficult to
                determine the actual location where the personal data is stored.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              Managing Contacts and Sending Messages
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                This type of service makes it possible to manage a database of
                email contacts, phone contacts, or any other contact information
                to communicate with the user.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                These services may also collect data concerning the date and
                time when the message was viewed by the user and when the user
                interacted with it, such as by clicking on links included in the
                message.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>Spam Protection</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                This type of service analyzes the traffic of the RetinaLogik
                website and the RetinaLogik application, potentially containing
                users' personal data, with the purpose of filtering it from
                parts of traffic, messages, and content that are recognized as
                spam.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              Managing Contacts and Sending Messages
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                This type of service makes it possible to manage a database of
                email contacts, phone contacts, or any other contact information
                to communicate with the user.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                These services may also collect data concerning the date and
                time when the message was viewed by the user and when the user
                interacted with it, such as by clicking on links included in the
                message.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              User Database Management
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                This type of service allows RetinaLogik to build user profiles
                by starting from an email address, a personal name, or other
                information that the user provides to this application and then
                tracking user activities through analytics features.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                Some of these services may also enable sending timed messages to
                the user, such as emails based on specific actions performed on
                the RetinaLogik website and RetinaLogik web application.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            12. Further Information about Personal Data
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              The Rights of Users
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                Users may exercise certain rights regarding their data processed
                by RetinaLogik. In particular, users have the right to do the
                following:
              </Typography>
              <Box sx={{ fontSize: "14px", pl: "16px" }}>
                <ul
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <li>
                    •{" "}
                    <span style={{ fontWeight: 600 }}>
                      Withdraw their consent at any time.
                    </span>{" "}
                    Users have the right to withdraw consent after they have
                    previously given their consent to the processing of their
                    personal data.
                  </li>
                  <li>
                    •{" "}
                    <span style={{ fontWeight: 600 }}>
                      Object to processing of their data.
                    </span>{" "}
                    Users have the right to object to the processing of their
                    data if the processing is carried out on a legal basis other
                    than consent.
                  </li>
                  <li>
                    •{" "}
                    <span style={{ fontWeight: 600 }}>Access their data.</span>{" "}
                    Users have the right to learn if RetinaLogik is processing
                    their data, obtain disclosure regarding certain aspects of
                    the processing, and obtain a copy of the data undergoing
                    processing.
                  </li>
                  <li>
                    •{" "}
                    <span style={{ fontWeight: 600 }}>
                      Verify and seek rectification.
                    </span>{" "}
                    Users have the right to verify their data accuracy and ask
                    for it to be updated or corrected.
                  </li>
                  <li>
                    •{" "}
                    <span style={{ fontWeight: 600 }}>
                      Restrict the processing of their data.
                    </span>{" "}
                    Users have the right, under certain circumstances, to
                    restrict the processing of their data. In this case,
                    RetinaLogik will not process their data for any purpose
                    other than storing it.
                  </li>
                  <li>
                    •{" "}
                    <span style={{ fontWeight: 600 }}>
                      Have their personal data deleted or otherwise removed.
                    </span>{" "}
                    Users have the right, under certain circumstances, to obtain
                    the erasure of their data from RetinaLogik.
                  </li>
                  <li>
                    •{" "}
                    <span style={{ fontWeight: 600 }}>
                      Receive their data and have it transferred to another
                      controller.
                    </span>{" "}
                    Users have the right to receive their data in a structured,
                    commonly used, machine-readable format, and, if technically
                    feasible, to have it transmitted to another controller
                    without any hindrance. This provision is applicable provided
                    that the data is processed by automated means and that the
                    processing is based on the user's consent, on a contract
                    that the user is part of, or on precontractual obligations.
                  </li>
                  <li>
                    •{" "}
                    <span style={{ fontWeight: 600 }}>Lodge a complaint.</span>{" "}
                    Users have the right to bring a claim before their competent
                    data protection authority.
                  </li>
                </ul>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            13. Details About the Right to Object to Processing
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              Where personal data is processed for the public interest, in the
              exercise of an official authority vested in RetinaLogik or for the
              legitimate interests pursued by RetinaLogik, users may object to
              such processing by providing a ground related to their particular
              situation to justify the objection.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              However, users must know that should their personal data be
              processed for direct marketing purposes, they can object to that
              processing at any time without providing any justification. To
              learn whether the RetinaLogik is processing Personal Data for
              direct marketing purposes, users may refer to the relevant
              sections of this document.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              For California residents, RetinaLogik does not sell or share
              personal health information for cross-context behavioral
              advertising. California residents may request information about
              the categories of personal information collected and the purposes
              for which it is used.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              How to Exercise These Rights
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                Any requests to exercise user rights can be directed to
                RetinaLogik through the contact details provided in this
                document (privacy@retinalogik.ca). These requests can be
                exercised free of charge and will be addressed by RetinaLogik as
                early as possible and always within one month.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>Cookie Policy</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                The RetinaLogik website and RetinaLogik web application use
                cookies.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                To learn more and for a detailed cookie notice, the user may
                consult the Cookie Policy.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            14. Additional Information about Data Collection and Processing
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>Legal Action</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                Users' personal data may be used for legal purposes by
                RetinaLogik in court or the stages leading to possible legal
                action arising from improper use of this application or the
                related services. The users declare they are aware that
                RetinaLogik may be required to reveal personal data upon request
                of public authorities.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              System Logs and Maintenance
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                For operation and maintenance purposes, this application and any
                third-party services may collect files that record interaction
                with this application (e.g., system logs) using other personal
                data (e.g., IP Address) for this purpose.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              How “Do Not Track” Requests are Handled
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                This application does not support “Do Not Track” requests.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                To determine whether any of the third-party services it uses
                honor “Do Not Track” requests, users should read their privacy
                policies.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            15. Changes to This Privacy Notice
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              RetinaLogik reserves the right to make changes to this privacy
              notice at any time by giving notice to users on this page and
              possibly within this application or - as far as technically and
              legally feasible - sending a notice to users via any contact
              information available to RetinaLogik. Users are strongly
              recommended to check this page often, referring to the date of the
              last modification listed at the bottom. Should the changes elect
              processing activities performed based on the users' consent,
              RetinaLogik shall collect new consent from the user where
              required.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            16. Legal Information
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              This privacy notice has been prepared based on provisions of
              multiple legislations, including Art. 13/14 of Regulation (EU)
              2016/679 (General Data Protection Regulation). Where required
              under applicable law, RetinaLogik will appoint an EU and/or UK
              representative for data protection matters. This privacy notice
              relates to the RetinaLogik website, application, and supporting
              services unless otherwise stated within this document.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>17. Contact Us</Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              To exercise your rights or contact our Data Privacy Office, please
              reach us at: privacy@retinalogik.ca.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;
